import { NoAvailableAccounts, SelectedAccount } from '@accounts'
import { CurrencyBadge } from '@components'
import settings from '@config/settings'
import { REGEX } from '@constants'
import {
  CountrySupportedPurposes,
  MerchantEntityTypes,
  paths,
  selectMerchantEntityType,
  useAppSelector,
  useCountries,
  useIbanRequestQry
} from 'mmfintech-backend-api'
import type { BankAccountIssuingPreviewRequest, BankAccountScheme } from 'mmfintech-backend-api/types'
import { formatMoney, GlobalContext, isValidArray, isValidFunction, isValidString, tr } from 'mmfintech-commons'
import { Button, ErrorDisplay, Input } from 'mmfintech-portal-commons'
import { useContext, useState } from 'react'
import toast from 'react-hot-toast'
import { useHistory } from 'react-router-dom'
import './IbanRequestModal.scss'

const IBAN_REQUEST_STEP = Object.freeze({
  SCHEME_SELECTION: 1,
  QUESTIONNAIRE: 2
} as const)

interface Props {
  reload?: () => void
}

function IbanRequestModal({ reload }: Props) {
  const { modalHide } = useContext(GlobalContext)

  const entityType = useAppSelector(selectMerchantEntityType)
  const isCompany = entityType === MerchantEntityTypes.COMPANY
  const isIndividual = entityType === MerchantEntityTypes.INDIVIDUAL

  const history = useHistory()

  const [step, setStep] = useState<number>(IBAN_REQUEST_STEP.SCHEME_SELECTION)

  const { countryOptions, countriesFetching } = useCountries(CountrySupportedPurposes.WITHDRAWAL)
  const [countryListError, setCountryListError] = useState<string>('')

  const {
    selectedScheme,
    setSelectedScheme,
    selectedCurrency,
    setSelectedCurrency,
    selectedAccount,
    setSelectedAccount,
    availableSchemes,
    ibanSchemeCurrencies,
    ibanSchemeCurrenciesError,
    ibanSchemeCurrenciesFetching,
    fees,
    // feesError,
    // feesFetching,
    accounts,
    accountsFetching,
    handleRequest,
    previewError,
    previewFetching,
    // previewResponse,
    initiateError,
    initiateFetching,
    // initiateResponse,
    resetMutations,
    setCountryList,
    countryList,
    formValues
  } = useIbanRequestQry({
    shouldSkipIssuingFee: isCompany || isIndividual,
    onFee: (payload: BankAccountIssuingPreviewRequest) => {
      modalHide()
      history.push(paths.ibanIssuingPreview(), { payload })
    },
    onSuccess: () => {
      isValidFunction(reload) && reload()
      modalHide()
      resetMutations()
      toast.remove()
      toast.success(
        'Your IBAN issuing request has been accepted. ' +
          'You will receive confirmation once IBAN has been approved and issued.'
      )
    },
    hasQuestionnaire: isIndividual
  })

  return (
    <div className='iban-request-modal-wrapper'>
      <div className='title'>{tr('FRONTEND.IBAN_ISSUING.MODAL.TITLE', 'IBAN issuing request')}</div>

      {step === IBAN_REQUEST_STEP.SCHEME_SELECTION && (
        <div>
          <div className='instructions-title'>
            {tr('FRONTEND.IBAN_ISSUING.MODAL.INSTRUCTIONS', 'Choose a payment network and currency for your IBAN:')}
          </div>

          <div className='tabs-container'>
            {!ibanSchemeCurrenciesFetching &&
              isValidArray(availableSchemes) &&
              availableSchemes?.map((scheme, index) => (
                <div
                  key={`${scheme} - ${index}`}
                  onClick={() => setSelectedScheme(scheme as BankAccountScheme)}
                  className={`tab ${selectedScheme === scheme ? 'active' : ''}`}
                  data-test={`${scheme.toLowerCase()}-tab`}
                >
                  {scheme}
                </div>
              ))}
          </div>

          <div className='badge-container'>
            {isValidString(selectedScheme) &&
              ibanSchemeCurrencies[selectedScheme]?.map((currency: string) => (
                <CurrencyBadge
                  key={`${selectedScheme}-${currency}`}
                  currencyCode={currency}
                  isSelected={selectedCurrency === currency}
                  onClick={() => setSelectedCurrency(currency)}
                />
              ))}
          </div>

          {isValidString(selectedScheme) && isValidString(selectedCurrency) && (
            <>
              <div className='associate-iban-title'>
                {tr(
                  'FRONTEND.IBAN_ISSUING.MODAL.ASSOCIATE.TITLE',
                  'Choose a currency account to associate with the IBAN:'
                )}
              </div>
              {/*<div className='subtitle'>
            {tr(
              'FRONTEND.IBAN_ISSUING.MODAL.ASSOCIATE.SUBTITLE',
              'If no selection is made, a new currency account will be created automatically.'
            )}
          </div>*/}

              <div className='iban-accounts-container'>
                {isValidArray(accounts) ? (
                  <div className='inner'>
                    {accounts?.map(acc => (
                      <SelectedAccount
                        account={acc}
                        setAccount={acc => {
                          if (acc.id === selectedAccount?.id) {
                            setSelectedAccount(undefined)
                          } else {
                            setSelectedAccount(acc)
                          }
                        }}
                        isSelected={selectedAccount?.id === acc.id}
                        key={acc.id}
                      />
                    ))}
                  </div>
                ) : !accountsFetching ? (
                  <NoAvailableAccounts currency={selectedCurrency} reload={reload} />
                ) : null}
              </div>
            </>
          )}

          <div className='fees-container'>
            <div className='subtitle'>
              {isCompany || isIndividual
                ? tr(
                    'FRONTEND.IBAN_ISSUING.MODAL.DISCLAIMER_COMPANY',
                    'You may need to update your existing or provide extra documents. Contact your customer support manager for more information regarding your request.'
                  )
                : tr(
                    'FRONTEND.IBAN_ISSUING.MODAL.DISCLAIMER',
                    'Please note that the Iban fee will be deducted from your account. You may need to update your existing or provide extra documents. Contact your customer support manager for more information regarding your request.'
                  )}
            </div>

            {isCompany || isIndividual ? (
              <div className='subtitle'>
                <b>{tr('FRONTEND.IBAN_ISSUING.COMPANY_LABEL', 'Issuing and maintenance fees')}:</b>{' '}
                {isValidArray(fees) ? (
                  fees.map((fee, index) => {
                    return (
                      <div key={`${fee.feeType} - ${index}`} className='fee-container'>
                        <span>{tr(fee.feeType, fee.feeType.toLowerCase().replace(REGEX.UNDERSCORE, ' '))}</span>
                        <span>{formatMoney(fee.value, fee.currency)}</span>
                      </div>
                    )
                  })
                ) : (
                  // tr('FRONTEND.IBAN_ISSUING.COMPANY_VALUE', 'Contact account manager')
                  <a href={settings.customerServiceUrl} rel='noopener noreferrer' target='_blank'>
                    {tr('FRONTEND.IBAN_ISSUING.INDIVIDUAL_VALUE', 'Customer Service')}
                  </a>
                )}
              </div>
            ) : isValidArray(fees) && isValidString(selectedCurrency) ? (
              <div className='fees'>
                {fees.map((fee, index) => {
                  const { feeType, feeCurrency, value } = fee
                  return (
                    <div className='fee-title' key={`${feeType} - ${index}`}>
                      <div>
                        {tr(
                          `FRONTEND.IBAN_ISSUING.${feeType}`,
                          feeType === 'IBAN_ISSUING_FEE' ? 'Issuing fee:' : 'Monthly maintenance fee:'
                        )}
                      </div>
                      <div className='fee'>{formatMoney(value, feeCurrency)}</div>
                    </div>
                  )
                })}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {step === IBAN_REQUEST_STEP.QUESTIONNAIRE && (
        <div>
          <Input
            name='countryList'
            type='select'
            value={countryList}
            options={countryOptions}
            data-test='file-type'
            label={tr('FRONTEND.IBAN_QUESTIONNAIRE.FORM.COUNTRY', 'Countries from which you will be funding the IBAN')}
            onChange={(_name: any, value: any) => {
              setCountryListError('')
              setCountryList(value)
            }}
            placeholder=''
            isLoading={countriesFetching}
            isMultiSelect
            error={countryListError}
            required
          />

          <Input
            name='yearlyTransactionsAmountInEur'
            type='number'
            label={tr(
              'FRONTEND.IBAN_QUESTIONNAIRE.FORM.AMOUNT',
              'Average value of your transaction(s) (EUR) in a year(12 months)'
            )}
            required
            {...formValues.registerInput('yearlyTransactionsAmountInEur')}
          />

          <Input
            name='totalNumOfBankAccounts'
            type='number'
            label={tr(
              'FRONTEND.IBAN_QUESTIONNAIRE.FORM.AMOUNT',
              'How many bank accounts are you holding at the moment?'
            )}
            required
            {...formValues.registerInput('totalNumOfBankAccounts')}
          />

          <Input
            name='linkedBankAccountsCount'
            type='number'
            label={tr(
              'FRONTEND.IBAN_QUESTIONNAIRE.FORM.ACCOUNTS_NUMBER',
              'How many payment methods/bank accounts will be used to transact through this IBAN?'
            )}
            required
            {...formValues.registerInput('linkedBankAccountsCount')}
          />

          <Input
            name='occupation'
            type='text'
            label={tr('FRONTEND.IBAN_QUESTIONNAIRE.FORM.OCCUPATION', 'Occupation')}
            required
            {...formValues.registerInput('occupation')}
          />

          <Input
            name='purposeOfAccount'
            type='text'
            label={tr('FRONTEND.IBAN_QUESTIONNAIRE.FORM.PURPOSE', 'Purpose of this account')}
            required
            {...formValues.registerInput('purposeOfAccount')}
          />
        </div>
      )}

      <ErrorDisplay error={[ibanSchemeCurrenciesError, previewError, initiateError]} />

      <div className='iban-request-buttons'>
        <Button
          color='secondary'
          text={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
          onClick={() => {
            modalHide()
            resetMutations()
          }}
          disabled={previewFetching || initiateFetching}
          data-test='button-cancel'
        />
        <Button
          color='primary'
          text={tr('FRONTEND.BUTTONS.REQUEST', 'Request')}
          onClick={() => {
            if (isIndividual && step === IBAN_REQUEST_STEP.SCHEME_SELECTION) {
              setStep(IBAN_REQUEST_STEP.QUESTIONNAIRE)
            } else {
              if (isIndividual && !isValidArray(countryList)) {
                setCountryListError(tr('VALIDATION.ERRORS.REQUIRED', 'This field should not be empty'))
              }
              if (isIndividual && (!formValues.areValid() || !isValidArray(countryList))) return
              handleRequest()
            }
          }}
          data-test='button-new-account'
          loading={previewFetching || initiateFetching}
          disabled={!isValidString(selectedScheme) || !isValidString(selectedCurrency) || !selectedAccount}
        />
      </div>
    </div>
  )
}

export default IbanRequestModal
